import {
  LifeReadinessSubcategory,
  LifeReadinessQuestion,
  LifeReadinessQuestionUserLevel,
  LifeReadinessCategory,
} from '@backrs_ui/common-ui';

import { LifeReadinessCategoryCardProps } from '../molecules/CategoryCard';

export const getQuestionCountsForSubcategory = (
  subcategory: LifeReadinessSubcategory,
  questions: LifeReadinessQuestion[],
  userLevels: LifeReadinessQuestionUserLevel[],
) => {
  return countForSubcategory(subcategory, questions, userLevels);
};

type LevelCounts = Readonly<{
  level1_count: number;
  level2_count: number;
  level3_count: number;
  level4_count: number;
  level5_count: number; // not actually used in the UI, but we do have a 5 level description so it needs to match otherwise TS complains
  total: number;
}>;

/**
 * Count how many times each level was selected
 * for questions in a specific subcategory.
 */
function countForSubcategory(
  subcategory: LifeReadinessSubcategory,
  questions: LifeReadinessQuestion[],
  userLevels: LifeReadinessQuestionUserLevel[],
): LevelCounts {
  const counts = {
    level1_count: 0,
    level2_count: 0,
    level3_count: 0,
    level4_count: 0,
    level5_count: 0,
    total: 0,
  };

  // Collect the set of question-IDs belonging to this subcategory
  const questionIdsInSub = new Set(
    questions.filter((q) => q.life_survey_subcategory_id === subcategory.id).map((q) => q.id),
  );

  // Tally up userLevels whose question is in that set
  for (const ul of userLevels) {
    if (!questionIdsInSub.has(ul.life_survey_question_id)) continue;

    const key = `level${ul.level}_count` as keyof LevelCounts;

    counts[key] += 1;
  }

  counts.total = questionIdsInSub.size;

  return counts;
}

/**
 * Build the progressBars map for a category,
 * or return undefined if there’s no data at all.
 */
export function getProgressCountsForCategory(
  category: LifeReadinessCategory,
  allSubcategories: LifeReadinessSubcategory[],
  allQuestions: LifeReadinessQuestion[],
  allUserLevels: LifeReadinessQuestionUserLevel[],
): LifeReadinessCategoryCardProps['progressBars'] | undefined {
  const subcategoriesInCategory = allSubcategories.filter(
    (sc) => sc.life_survey_category_id === category.id,
  );

  // for each subcategory, compute its counts for how many answers to each level type
  const nameToCounts = subcategoriesInCategory.map((subcategory) => ({
    name: subcategory.name,
    counts: countForSubcategory(subcategory, allQuestions, allUserLevels),
  }));

  // Sum up levels 1–4 across all subcategories to see if there’s any data
  const totalLevel1to4 = nameToCounts.reduce((sum, { counts }) => {
    return (
      sum + counts.level1_count + counts.level2_count + counts.level3_count + counts.level4_count
    );
  }, 0);

  // If no progress at levels 1–4, return undefined so that we can display CTA
  if (totalLevel1to4 === 0) return undefined;

  const progressBars: Record<string, Omit<LevelCounts, 'level5_count'>> = {};

  for (const { name, counts } of nameToCounts) {
    progressBars[name] = {
      level1_count: counts.level1_count,
      level2_count: counts.level2_count,
      level3_count: counts.level3_count,
      level4_count: counts.level4_count,
      total: counts.total,
    };
  }

  return progressBars;
}
